"use client";

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Chip, Kv, Panel, PanelHead, Pill } from "@/components/desktop/chrome";
import { api, toastCopy, type CreditTask } from "@/lib/client-api";
import type { Permissions } from "@/lib/auth/roles";
import { minRoleFor } from "@/lib/auth/roles";

export function QueueView({
  onOpenCustomer,
  onOpenBusiness,
}: {
  onOpenCustomer: () => void;
  onOpenBusiness: () => void;
}) {
  return (
    <Panel>
      <PanelHead title="My queue" />
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
            <th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2">Channel</th>
            <th className="px-3 py-2">Wait</th>
            <th className="px-3 py-2">Predicted reason</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-[#e6ebf0]">
            <td className="px-3 py-2">Maria Delgado</td>
            <td className="px-3 py-2">Voice</td>
            <td className="px-3 py-2 num">0:00 — active</td>
            <td className="px-3 py-2">Higher bill — roaming (86%)</td>
            <td className="px-3 py-2"><Button size="sm" variant="link" onClick={onOpenCustomer}>Open</Button></td>
          </tr>
          <tr className="border-t border-[#e6ebf0]">
            <td className="px-3 py-2">Harborline Logistics <Chip>Business</Chip></td>
            <td className="px-3 py-2">Voice</td>
            <td className="px-3 py-2 num">Scheduled 10:45</td>
            <td className="px-3 py-2">Site A circuit degradation (78%)</td>
            <td className="px-3 py-2"><Button size="sm" variant="link" onClick={onOpenBusiness}>Open</Button></td>
          </tr>
          <tr className="border-t border-[#e6ebf0]">
            <td className="px-3 py-2">D. Okafor</td>
            <td className="px-3 py-2">Chat</td>
            <td className="px-3 py-2 num">1:12</td>
            <td className="px-3 py-2">Device upgrade eligibility (71%)</td>
            <td className="px-3 py-2"><Button size="sm" variant="link" disabled>Open</Button></td>
          </tr>
          <tr className="border-t border-[#e6ebf0]">
            <td className="px-3 py-2">S. Nguyen</td>
            <td className="px-3 py-2">Callback</td>
            <td className="px-3 py-2 num">Scheduled 11:30</td>
            <td className="px-3 py-2">Move / transfer service (64%)</td>
            <td className="px-3 py-2"><Button size="sm" variant="link" disabled>Open</Button></td>
          </tr>
        </tbody>
      </table>
    </Panel>
  );
}

export function ReportsView({
  canApprove,
  onJournalRefresh,
}: {
  canApprove: boolean;
  onJournalRefresh: () => Promise<void>;
}) {
  const [tasks, setTasks] = useState<CreditTask[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refreshApprovals = useCallback(async () => {
    if (!canApprove) {
      setTasks([]);
      return;
    }
    const data = await api.approvals();
    setTasks(data.tasks);
  }, [canApprove]);

  useEffect(() => {
    void refreshApprovals();
  }, [refreshApprovals]);

  async function decide(id: string, decision: "approve" | "deny") {
    setBusy(id);
    try {
      const result = await api.decideApproval(id, decision);
      await refreshApprovals();
      await onJournalRefresh();
      const action = decision === "approve" ? "credit.approved" : "credit.denied";
      toast(
        toastCopy(action, {
          summary:
            result.status === "posted"
              ? `$${result.amount.toFixed(2)} approved — posts to next invoice`
              : `$${result.amount.toFixed(2)} denied — credit was not posted`,
        }),
      );
    } finally {
      setBusy(null);
    }
  }
  return (
    <div className="space-y-3.5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {[
          { label: "Interactions today", value: "1,284", meta: "Voice 58% · Chat 31% · Other 11%" },
          { label: "First contact resolution", value: "78.4%", meta: "Target 80%" },
          { label: "Avg handle time", value: "6m 42s", meta: "Down 18s wk/wk" },
          { label: "Prediction accuracy", value: "81%", meta: "Top-1 contact reason match" },
        ].map((stat) => (
          <div key={stat.label} className="rounded-[10px] border border-border bg-card px-3.5 py-3">
            <div className="text-[11.5px] font-semibold text-muted-foreground">{stat.label}</div>
            <div className="mt-0.5 font-[family-name:var(--font-heading)] text-[21px] font-bold num">{stat.value}</div>
            <div className="mt-0.5 text-[11.5px] text-[#43535f]">{stat.meta}</div>
          </div>
        ))}
      </div>
      <Panel>
        <PanelHead title="Top contact reasons — today">
          <span className="flex-1" />
          <span className="text-[11.5px] text-muted-foreground">From interaction journal + Helix categorization</span>
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">Volume</th>
              <th className="px-3 py-2">FCR</th>
              <th className="px-3 py-2">Trend</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Billing — unexpected charges</td><td className="px-3 py-2 num">312</td><td className="px-3 py-2 num">82%</td><td className="px-3 py-2"><Pill tone="warn">▲ 24% (roaming season)</Pill></td></tr>
            <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Network — outage node CHI-114</td><td className="px-3 py-2 num">207</td><td className="px-3 py-2 num">91%</td><td className="px-3 py-2"><Pill tone="alert">▲ new event</Pill></td></tr>
            <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Device — upgrade & trade-in</td><td className="px-3 py-2 num">184</td><td className="px-3 py-2 num">74%</td><td className="px-3 py-2"><Pill>flat</Pill></td></tr>
            <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Account — plan changes</td><td className="px-3 py-2 num">141</td><td className="px-3 py-2 num">80%</td><td className="px-3 py-2"><Pill>flat</Pill></td></tr>
          </tbody>
        </table>
      </Panel>
      <Panel>
        <PanelHead title="Approvals waiting" />
        {!canApprove && (
          <p className="px-4 py-6 text-[13px] text-muted-foreground">
            Credit approvals are limited to Supervisors. IT can see team reports but not customer credit tasks.
          </p>
        )}
        {canApprove && tasks && tasks.length === 0 && (
          <p className="px-4 py-6 text-[13px] text-muted-foreground">No credits waiting for approval.</p>
        )}
        {canApprove && tasks && tasks.length > 0 && (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Request</th>
              <th className="px-3 py-2">Agent</th>
              <th className="px-3 py-2">Amount</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {tasks.map((task) => (
              <tr key={task.id} className="border-t border-[#e6ebf0]">
                <td className="px-3 py-2">{task.reason}</td>
                <td className="px-3 py-2">{task.requestedBy.role}</td>
                <td className="px-3 py-2 num">${task.amount.toFixed(2)}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1.5">
                    <Button size="sm" disabled={busy === task.id} onClick={() => void decide(task.id, "approve")}>Approve</Button>
                    <Button size="sm" variant="outline" disabled={busy === task.id} onClick={() => void decide(task.id, "deny")}>Deny</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </Panel>
    </div>
  );
}

export function BusinessView({
  perms,
  onCustomer,
  onTicket,
  onCredit,
  onAction,
}: {
  perms: Permissions;
  onCustomer: () => void;
  onTicket: () => void;
  onCredit: () => void;
  onAction: (action: string, summary: string) => void;
}) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-4 rounded-[10px] border border-border bg-card px-4 py-3.5">
        <div>
          <h1 className="text-[19px] font-bold">Harborline Logistics</h1>
          <p className="mt-0.5 text-[12.5px] text-[#43535f]">
            Business account <span className="num">00883-011522-04</span> · Parent + 3 sites · Internet, voice, video, mobile fleet · Customer since 2016
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Chip tone="ok">Good standing</Chip>
          <Chip tone="alert">Open service problem — Site A</Chip>
          <Chip tone="ok">Contact verified: T. Boyd (technical admin)</Chip>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={onCustomer}>Switch account</Button>
          <Button onClick={onTicket}>Create ticket</Button>
        </div>
      </div>
      <div className="mt-4 grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="space-y-3.5">
          <Panel>
            <PanelHead title="Account hierarchy">
              <span className="flex-1" />
              <span className="text-[11.5px] text-muted-foreground">Consolidated invoice · site-level balances</span>
            </PanelHead>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Site</th>
                  <th className="px-3 py-2">Services</th>
                  <th className="px-3 py-2">Access</th>
                  <th className="px-3 py-2">Balance</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Headquarters — Chicago, IL</td><td className="px-3 py-2">Fiber 2G · SIP voice (24 trunks) · IP video (lobby set-top)</td><td className="px-3 py-2">FTTH</td><td className="px-3 py-2 num">$2,140.00</td><td className="px-3 py-2"><Pill tone="ok">Active</Pill></td></tr>
                <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Site A — Milwaukee, WI</td><td className="px-3 py-2">Fiber 1G · SIP voice (8 trunks)</td><td className="px-3 py-2">FTTH</td><td className="px-3 py-2 num">$860.00</td><td className="px-3 py-2"><Pill tone="alert">Degraded</Pill></td></tr>
                <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Site B — Gary, IN</td><td className="px-3 py-2">Internet 600 · Digital voice · Video (4 legacy set-top boxes)</td><td className="px-3 py-2">HFC (coax)</td><td className="px-3 py-2 num">$412.50</td><td className="px-3 py-2"><Pill tone="ok">Active</Pill></td></tr>
                <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Site C — Rockford, IL</td><td className="px-3 py-2">Fiber 500 · IP video (2 IP set-tops + app)</td><td className="px-3 py-2">FTTH</td><td className="px-3 py-2 num">$298.00</td><td className="px-3 py-2"><Pill tone="ok">Active</Pill></td></tr>
              </tbody>
            </table>
          </Panel>
          <Panel>
            <PanelHead title="Service problem — Site A">
              <span className="flex-1" />
              <Pill tone="warn">PRB0007712</Pill>
            </PanelHead>
            <div className="px-4 py-3.5">
              <Kv
                wide
                items={[
                  { label: "Problem", value: "Circuit degradation on the Milwaukee fiber ring · packet loss 3–6% since 6:20 AM" },
                  { label: "Affected services", value: "Site A internet and SIP voice quality" },
                  { label: "Linked ticket", value: "INC0049288 (BMC Helix) · P2 · Network Ops" },
                  { label: "Dispatch", value: "Technician Mon Sep 7, 8–10 AM · confirmed with T. Boyd" },
                  { label: "Customer updates", value: "Status notices texted to the technical contact at each milestone" },
                ]}
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => onAction("notice.sent", "Site A problem update texted to T. Boyd")}>Send status update</Button>
                <Button size="sm" variant="outline" onClick={() => onAction("appointment.viewed", "Site A dispatch Sep 7, 8-10 AM viewed")}>View dispatch</Button>
                <Button size="sm" variant="outline" disabled={!perms.adjust} onClick={onCredit}>Apply SLA credit</Button>
              </div>
            </div>
          </Panel>
          <Panel>
            <PanelHead title="Authorized contacts">
              <span className="flex-1" />
              <span className="text-[11.5px] text-muted-foreground">What each person may do on this account</span>
            </PanelHead>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Scope</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Tom Boyd</td><td className="px-3 py-2">Technical admin</td><td className="px-3 py-2">All sites · service changes, tickets, dispatch</td></tr>
                <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Lena Marsh</td><td className="px-3 py-2">Billing contact</td><td className="px-3 py-2">Invoices, payments, disputes · no service changes</td></tr>
                <tr className="border-t border-[#e6ebf0]"><td className="px-3 py-2">Rosa Chen</td><td className="px-3 py-2">Site manager</td><td className="px-3 py-2">Site B only · tickets and appointments</td></tr>
              </tbody>
            </table>
          </Panel>
          <Panel>
            <PanelHead title="Mobile fleet">
              <span className="flex-1" />
              <span className="text-[11.5px] text-muted-foreground">24 lines · pooled 400 GB</span>
            </PanelHead>
            <div className="px-4 py-3.5">
              <Kv
                wide
                items={[
                  { label: "Devices", value: "14 on payment plans · 6 purchased in full · 4 BYOD" },
                  { label: "Pooled data used", value: "288 GB of 400 GB (72%)" },
                  { label: "Pending request", value: "Fleet expansion, 12 additional lines · quote QTE-2210" },
                ]}
              />
              <p className="mt-2 text-xs text-muted-foreground">Quotes, ordering, and eligibility checks stay in existing tools for now — planned for a future release of this console.</p>
            </div>
          </Panel>
        </div>
        <aside className="space-y-3.5">
          <Panel>
            <PanelHead title="Suggested actions" />
            <div className="flex flex-col">
              <button type="button" className="border-b border-[#e6ebf0] px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary" onClick={() => onAction("notice.sent", "Site A problem update texted to T. Boyd")}>Send Site A status update</button>
              <button type="button" className="border-b border-[#e6ebf0] px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary" onClick={() => onAction("appointment.offered", "Alternate dispatch windows presented")}>Reschedule dispatch window</button>
              <button type="button" disabled={!perms.adjust} className="flex items-center border-b border-[#e6ebf0] px-4 py-2.5 text-left text-[13px] font-medium hover:bg-secondary disabled:opacity-45" onClick={onCredit}>
                Apply SLA credit <span className="ml-auto text-[11px] text-muted-foreground">{perms.adjust ? `up to $${perms.adjustLimit}` : `Needs ${minRoleFor("adjust")}`}</span>
              </button>
              <button type="button" disabled className="flex items-center px-4 py-2.5 text-left text-[13px] font-medium opacity-45">
                Update fleet quote <span className="ml-auto text-[11px]">Planned — use legacy tool</span>
              </button>
            </div>
          </Panel>
        </aside>
      </div>
    </div>
  );
}

export function AdminView({
  allowed,
  onAction,
}: {
  allowed: boolean;
  onAction: (action: string, summary: string) => void;
}) {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.adminMatrix>> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    api.adminMatrix().then(setData).catch((err: Error) => setError(err.message));
  }, [allowed]);

  if (!allowed) {
    return (
      <Panel>
        <div className="px-4 py-8 text-center text-[13px] text-[#43535f]">
          The admin console is limited to IT administrators. Customer routes stay blocked for this role.
        </div>
      </Panel>
    );
  }
  if (error) {
    return <Panel><div className="px-4 py-8 text-center text-alert">{error}</div></Panel>;
  }
  if (!data) {
    return <Panel><div className="px-4 py-8 text-center text-muted-foreground">Loading admin console…</div></Panel>;
  }

  const headers = ["CSR L1", "CSR L2", "CSR L3", "Supervisor", "Back Office", "IT"];

  return (
    <div className="space-y-3.5">
      <Panel>
        <PanelHead title="Users & roles">
          <span className="flex-1" />
          <Button size="sm" onClick={() => onAction("admin.userEdited", "New user form opened")}>Add user</Button>
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">MFA</th>
              <th className="px-3 py-2">Last sign-in</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.users.map((user) => (
              <tr key={user.name} className="border-t border-[#e6ebf0]">
                <td className="px-3 py-2">{user.name}</td>
                <td className="px-3 py-2">{user.role}</td>
                <td className="px-3 py-2"><Pill tone={user.status.startsWith("Locked") ? "warn" : "ok"}>{user.status}</Pill></td>
                <td className="px-3 py-2 text-ok">{user.mfa ? "✓" : "—"}</td>
                <td className="px-3 py-2">{user.lastSignIn}</td>
                <td className="px-3 py-2"><Button size="sm" variant="link" onClick={() => onAction("admin.userEdited", `Edit user ${user.name}`)}>Edit</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel>
        <PanelHead title="Role permission matrix">
          <span className="flex-1" />
          <span className="text-[11.5px] text-muted-foreground">Least-privilege defaults · changes require dual approval</span>
        </PanelHead>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-secondary text-left text-[11.5px] font-semibold text-muted-foreground">
              <th className="px-3 py-2">Capability</th>
              {headers.map((h) => <th key={h} className="px-3 py-2">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.capabilities.map((row) => (
              <tr key={row.name} className="border-t border-[#e6ebf0]">
                <td className="px-3 py-2">{row.name}</td>
                {row.values.map((value, i) => (
                  <td key={`${row.name}-${i}`} className={`px-3 py-2 ${value === "—" ? "text-muted-foreground" : value === "✓" ? "font-semibold text-ok" : "font-semibold text-warn"}`}>{value}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="px-4 py-2.5 text-[11.5px] text-muted-foreground">IT administers the system but has no standing access to customer data (separation of duties). All permission changes are journaled.</p>
      </Panel>
    </div>
  );
}

export function EmptySearch({ query, mockHints }: { query: string; mockHints?: boolean }) {
  return (
    <Panel>
      <div className="px-4 py-10 text-center">
        <h2 className="text-base font-semibold">No customer matched</h2>
        <p className="mt-2 text-[13px] text-[#43535f]">
          {mockHints
            ? `Nothing matched “${query}”. Try Maria, Delgado, or account 00447-190288-15.`
            : `Nothing matched “${query}”. Search by name, phone, or a 13-digit account number.`}
        </p>
      </div>
    </Panel>
  );
}

export function IdleSearch() {
  return (
    <Panel>
      <div className="px-4 py-10 text-center">
        <h2 className="text-base font-semibold">Search to open Customer 360</h2>
        <p className="mt-2 text-[13px] text-[#43535f]">
          Enter a name, phone, or Optimum account number. Identity stays masked until you verify this session.
        </p>
      </div>
    </Panel>
  );
}

export function ForbiddenCustomer() {
  return (
    <Panel>
      <div className="px-4 py-10 text-center">
        <h2 className="text-base font-semibold">Customer data is blocked</h2>
        <p className="mt-2 text-[13px] text-[#43535f]">
          IT administers users, roles, and configuration but has no standing permission to read customer accounts. Open the Admin console.
        </p>
      </div>
    </Panel>
  );
}
